import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  increment,
  onSnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { toServiceSlugs } from '../config/services';

/**
 * PETS
 */
export const createPet = async (ownerId, petData) => {
  try {
    const petRef = await addDoc(collection(db, 'pets'), {
      ...petData,
      ownerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      petId: petRef.id
    };
  } catch (error) {
    console.error('Create pet error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const updatePet = async (petId, updates) => {
  try {
    await updateDoc(doc(db, 'pets', petId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update pet error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const deletePet = async (petId) => {
  try {
    await deleteDoc(doc(db, 'pets', petId));
    return { success: true };
  } catch (error) {
    console.error('Delete pet error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getPetsByOwner = async (ownerId) => {
  try {
    const q = query(collection(db, 'pets'), where('ownerId', '==', ownerId));
    const snapshot = await getDocs(q);
    const pets = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return {
      success: true,
      pets
    };
  } catch (error) {
    console.error('Get pets error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * CAREGIVERS
 */
export const getCaregiverProfile = async (caregiverId) => {
  try {
    const caregiverDoc = await getDoc(doc(db, 'users', caregiverId));
    if (!caregiverDoc.exists()) {
      throw new Error('Caregiver not found');
    }
    return {
      success: true,
      caregiver: {
        id: caregiverDoc.id,
        ...caregiverDoc.data()
      }
    };
  } catch (error) {
    console.error('Get caregiver error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const searchCaregivers = async (filters = {}) => {
  try {
    let q = query(
      collection(db, 'users'),
      where('role', '==', 'caregiver')
    );

    // Add filters
    if (filters.location) {
      q = query(q, where('location', '==', filters.location));
    }
    if (filters.isAvailable) {
      q = query(q, where('isAvailable', '==', true));
    }

    const snapshot = await getDocs(q);
    const caregivers = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      caregivers
    };
  } catch (error) {
    console.error('Search caregivers error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * BOOKINGS
 */
export const createBooking = async (bookingData) => {
  try {
    const bookingRef = await addDoc(collection(db, 'bookings'), {
      ...bookingData,
      status: 'requested',
      source: 'platform',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      bookingId: bookingRef.id
    };
  } catch (error) {
    console.error('Create booking error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const updateBookingStatus = async (bookingId, status, updates = {}) => {
  try {
    await updateDoc(doc(db, 'bookings', bookingId), {
      status,
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update booking status error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getBookingsByOwner = async (ownerId) => {
  try {
    const q = query(
      collection(db, 'bookings'),
      where('ownerId', '==', ownerId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const bookings = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return {
      success: true,
      bookings
    };
  } catch (error) {
    console.error('Get bookings by owner error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getBookingsByCaregiver = async (caregiverId) => {
  try {
    const q = query(
      collection(db, 'bookings'),
      where('caregiverId', '==', caregiverId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const bookings = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return {
      success: true,
      bookings
    };
  } catch (error) {
    console.error('Get bookings by caregiver error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * OFFLINE JOBS
 */
export const createOfflineJob = async (userId, jobData) => {
  try {
    const jobRef = await addDoc(collection(db, 'offlineJobs'), {
      ...jobData,
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      jobId: jobRef.id
    };
  } catch (error) {
    console.error('Create offline job error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Admin: Get ALL offline jobs across all users
export const getAllOfflineJobs = async () => {
  try {
    const q = query(
      collection(db, 'offlineJobs'),
      orderBy('jobDate', 'desc')
    );
    const snapshot = await getDocs(q);
    const jobs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return {
      success: true,
      jobs
    };
  } catch (error) {
    console.error('Get all offline jobs error:', error);
    return {
      success: false,
      error: error.message,
      jobs: []
    };
  }
};

export const updateOfflineJob = async (jobId, updates) => {
  try {
    await updateDoc(doc(db, 'offlineJobs', jobId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update offline job error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const deleteOfflineJob = async (jobId) => {
  try {
    await deleteDoc(doc(db, 'offlineJobs', jobId));
    return { success: true };
  } catch (error) {
    console.error('Delete offline job error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getOfflineJobsByUser = async (userId) => {
  try {
    const q = query(
      collection(db, 'offlineJobs'),
      where('userId', '==', userId),
      orderBy('jobDate', 'desc')
    );
    const snapshot = await getDocs(q);
    const jobs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return {
      success: true,
      jobs
    };
  } catch (error) {
    console.error('Get offline jobs error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Admin: Get financial analytics and breakdown
export const getFinancialAnalytics = async () => {
  try {
    // Get all bookings
    const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
    const bookings = bookingsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Get all offline jobs
    const offlineJobsResult = await getAllOfflineJobs();
    const offlineJobs = offlineJobsResult.success ? offlineJobsResult.jobs : [];
    
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Calculate platform bookings revenue (completed bookings only)
    const completedBookings = bookings.filter(b => b.status === 'completed');
    const platformRevenue = completedBookings.reduce((sum, b) => {
      const commission = (b.escrowAmount || 0) * ((b.commissionPercent || 15) / 100);
      return sum + commission;
    }, 0);
    
    // Calculate total GMV (Gross Merchandise Value)
    const totalGMV = completedBookings.reduce((sum, b) => sum + (b.escrowAmount || 0), 0);
    
    // Calculate total caregiver earnings from platform
    const caregiverEarnings = completedBookings.reduce((sum, b) => {
      const earnings = (b.escrowAmount || 0) * (1 - (b.commissionPercent || 15) / 100);
      return sum + earnings;
    }, 0);
    
    // Calculate owner spending
    const ownerSpending = completedBookings.reduce((sum, b) => sum + (b.escrowAmount || 0), 0);
    
    // Calculate offline jobs revenue
    const offlineJobsRevenue = offlineJobs.reduce((sum, job) => sum + (job.amount || 0) + (job.taxAmount || 0), 0);
    
    // Earnings by caregiver (platform + offline)
    const earningsByCaregiver = {};
    
    // Platform earnings per caregiver
    completedBookings.forEach(booking => {
      const caregiverId = booking.caregiverId;
      if (!earningsByCaregiver[caregiverId]) {
        earningsByCaregiver[caregiverId] = {
          caregiverId,
          platformEarnings: 0,
          offlineEarnings: 0,
          totalEarnings: 0,
          platformJobs: 0,
          offlineJobs: 0,
          totalJobs: 0
        };
      }
      const earnings = (booking.escrowAmount || 0) * (1 - (booking.commissionPercent || 15) / 100);
      earningsByCaregiver[caregiverId].platformEarnings += earnings;
      earningsByCaregiver[caregiverId].platformJobs += 1;
    });
    
    // Offline earnings per caregiver
    offlineJobs.filter(job => job.userRole === 'caregiver').forEach(job => {
      const userId = job.userId;
      if (!earningsByCaregiver[userId]) {
        earningsByCaregiver[userId] = {
          caregiverId: userId,
          platformEarnings: 0,
          offlineEarnings: 0,
          totalEarnings: 0,
          platformJobs: 0,
          offlineJobs: 0,
          totalJobs: 0
        };
      }
      earningsByCaregiver[userId].offlineEarnings += (job.amount || 0) + (job.taxAmount || 0);
      earningsByCaregiver[userId].offlineJobs += 1;
    });
    
    // Calculate totals for each caregiver
    Object.values(earningsByCaregiver).forEach(caregiver => {
      caregiver.totalEarnings = caregiver.platformEarnings + caregiver.offlineEarnings;
      caregiver.totalJobs = caregiver.platformJobs + caregiver.offlineJobs;
      
      // Get caregiver name
      const caregiverUser = users.find(u => (u.id || u.uid) === caregiver.caregiverId);
      caregiver.name = caregiverUser?.displayName || caregiverUser?.username || caregiverUser?.email || 'Unknown';
      caregiver.email = caregiverUser?.email || '';
    });
    
    // Spending by owner
    const spendingByOwner = {};
    completedBookings.forEach(booking => {
      const ownerId = booking.ownerId;
      if (!spendingByOwner[ownerId]) {
        spendingByOwner[ownerId] = {
          ownerId,
          totalSpent: 0,
          bookingsCount: 0
        };
      }
      spendingByOwner[ownerId].totalSpent += (booking.escrowAmount || 0);
      spendingByOwner[ownerId].bookingsCount += 1;
      
      // Get owner name
      const ownerUser = users.find(u => (u.id || u.uid) === ownerId);
      spendingByOwner[ownerId].name = ownerUser?.displayName || ownerUser?.username || ownerUser?.email || 'Unknown';
      spendingByOwner[ownerId].email = ownerUser?.email || '';
    });
    
    // All transactions (for transaction history)
    const transactions = [
      ...completedBookings.map(b => ({
        id: b.id,
        date: b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt),
        type: 'Booking',
        userId: b.ownerId,
        userType: 'owner',
        amount: b.escrowAmount || 0,
        commission: (b.escrowAmount || 0) * ((b.commissionPercent || 15) / 100),
        status: b.status,
        source: 'platform'
      })),
      ...offlineJobs.map(job => ({
        id: job.id,
        date: job.jobDate ? new Date(job.jobDate) : new Date(),
        type: 'Offline Job',
        userId: job.userId,
        userType: job.userRole || 'caregiver',
        amount: (job.amount || 0) + (job.taxAmount || 0),
        commission: 0, // No commission on offline jobs
        status: 'completed',
        source: 'offline',
        serviceType: job.serviceType
      }))
    ].sort((a, b) => b.date - a.date);
    
    // Add user names to transactions
    transactions.forEach(txn => {
      const user = users.find(u => (u.id || u.uid) === txn.userId);
      txn.userName = user?.displayName || user?.username || user?.email || 'Unknown';
    });
    
    return {
      success: true,
      analytics: {
        platformRevenue,
        totalGMV,
        caregiverEarnings,
        ownerSpending,
        offlineJobsRevenue,
        totalBookings: completedBookings.length,
        totalOfflineJobs: offlineJobs.length,
        avgRevenuePerBooking: completedBookings.length > 0 ? (platformRevenue / completedBookings.length) : 0
      },
      earningsByCaregiver: Object.values(earningsByCaregiver).sort((a, b) => b.totalEarnings - a.totalEarnings),
      spendingByOwner: Object.values(spendingByOwner).sort((a, b) => b.totalSpent - a.totalSpent),
      transactions: transactions.slice(0, 50) // Last 50 transactions
    };
  } catch (error) {
    console.error('Get financial analytics error:', error);
    return {
      success: false,
      error: error.message,
      analytics: {},
      earningsByCaregiver: [],
      spendingByOwner: [],
      transactions: []
    };
  }
};

/**
 * EXPENSES
 */
export const createExpense = async (userId, expenseData) => {
  try {
    const expenseRef = await addDoc(collection(db, 'expenses'), {
      ...expenseData,
      userId,
      caregiverId: userId, // Keep for backwards compatibility
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      expenseId: expenseRef.id
    };
  } catch (error) {
    console.error('Create expense error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getExpensesByCaregiver = async (caregiverId) => {
  try {
    console.log('🔍 Fetching expenses for caregiverId:', caregiverId);
    
    // Try with userId first (new field), then fallback to caregiverId (old field)
    const q1 = query(
      collection(db, 'expenses'),
      where('userId', '==', caregiverId)
    );
    const q2 = query(
      collection(db, 'expenses'),
      where('caregiverId', '==', caregiverId)
    );
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    console.log('📊 Found with userId:', snapshot1.docs.length);
    console.log('📊 Found with caregiverId:', snapshot2.docs.length);
    
    // Combine and deduplicate expenses
    const expensesMap = new Map();
    
    snapshot1.docs.forEach(doc => {
      expensesMap.set(doc.id, { id: doc.id, ...doc.data() });
    });
    
    snapshot2.docs.forEach(doc => {
      if (!expensesMap.has(doc.id)) {
        expensesMap.set(doc.id, { id: doc.id, ...doc.data() });
      }
    });
    
    // Sort by date in JavaScript (no index needed)
    const expenses = Array.from(expensesMap.values()).sort((a, b) => {
      const dateA = new Date(a.date || a.createdAt?.toDate?.() || 0);
      const dateB = new Date(b.date || b.createdAt?.toDate?.() || 0);
      return dateB - dateA; // desc order
    });
    
    console.log('✅ Total expenses:', expenses.length);
    
    return {
      success: true,
      expenses
    };
  } catch (error) {
    console.error('Get expenses error:', error);
    return {
      success: false,
      error: error.message,
      expenses: []
    };
  }
};

export const updateExpense = async (expenseId, updates) => {
  try {
    await updateDoc(doc(db, 'expenses', expenseId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Update expense error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const deleteExpense = async (expenseId) => {
  try {
    await deleteDoc(doc(db, 'expenses', expenseId));
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Delete expense error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * REVIEWS
 */
export const createReview = async (reviewData) => {
  try {
    const reviewRef = await addDoc(collection(db, 'reviews'), {
      ...reviewData,
      createdAt: serverTimestamp()
    });

    // Update caregiver rating
    const caregiverRef = doc(db, 'users', reviewData.caregiverId);
    await updateDoc(caregiverRef, {
      totalReviews: increment(1),
      rating: reviewData.rating // You'll need to calculate average
    });
    
    return {
      success: true,
      reviewId: reviewRef.id
    };
  } catch (error) {
    console.error('Create review error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getReviewsByCaregiver = async (caregiverId) => {
  try {
    const q = query(
      collection(db, 'reviews'),
      where('caregiverId', '==', caregiverId),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    const snapshot = await getDocs(q);
    const reviews = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return {
      success: true,
      reviews
    };
  } catch (error) {
    console.error('Get reviews error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * COUPONS & FEATURE ACCESS
 */
export const getAllCoupons = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'coupons'));
    const coupons = snapshot.docs.map(doc => ({
      id: doc.id,
      code: doc.id, // The document ID is the coupon code
      ...doc.data()
    }));
    return {
      success: true,
      coupons
    };
  } catch (error) {
    console.error('Get coupons error:', error);
    return {
      success: false,
      error: error.message,
      coupons: []
    };
  }
};

export const createCoupon = async (couponData) => {
  try {
    await setDoc(doc(db, 'coupons', couponData.code), {
      ...couponData,
      createdAt: serverTimestamp(),
      usedCount: 0
    });
    return { success: true };
  } catch (error) {
    console.error('Create coupon error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const updateCoupon = async (couponCode, updates) => {
  try {
    await updateDoc(doc(db, 'coupons', couponCode), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update coupon error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const deleteCoupon = async (couponCode) => {
  try {
    await deleteDoc(doc(db, 'coupons', couponCode));
    return { success: true };
  } catch (error) {
    console.error('Delete coupon error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const validateAndRedeemCoupon = async (userId, couponCode, adminAssigned = false, userEmail = null) => {
  try {
    const couponDoc = await getDoc(doc(db, 'coupons', couponCode));
    
    if (!couponDoc.exists()) {
      return {
        success: false,
        error: 'Invalid coupon code'
      };
    }

    const coupon = couponDoc.data();

    // Validate coupon
    if (!coupon.active) {
      return {
        success: false,
        error: 'Coupon is no longer active'
      };
    }

    if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) {
      return {
        success: false,
        error: 'Coupon has expired'
      };
    }

    if (coupon.maxUses && coupon.usedCount >= coupon.maxUses) {
      return {
        success: false,
        error: `Coupon has reached maximum uses (${coupon.usedCount || 0}/${coupon.maxUses})`
      };
    }

    // Check if user already redeemed THIS SPECIFIC COUPON
    const redemptionsSnapshot = await getDocs(
      query(
        collection(db, 'couponRedemptions'),
        where('userId', '==', userId),
        where('couponCode', '==', couponCode)
      )
    );
    
    if (!redemptionsSnapshot.empty) {
      return {
        success: false,
        error: 'You have already redeemed this coupon'
      };
    }

    // Redeem coupon - calculate expiry
    const expiresAt = coupon.duration === 'lifetime' 
      ? null 
      : new Date(Date.now() + coupon.duration * 24 * 60 * 60 * 1000).toISOString();

    const redemptionId = doc(collection(db, 'couponRedemptions')).id;
    const redemptionData = {
      id: redemptionId,
      userId,
      userEmail: userEmail || 'Unknown',
      couponCode,
      feature: coupon.feature,
      duration: coupon.duration,
      redeemedAt: new Date().toISOString(),
      expiresAt,
      adminAssigned,
      active: true
    };

    // Create redemption record (for tracking/reporting)
    await setDoc(doc(db, 'couponRedemptions', redemptionId), redemptionData);

    // Grant feature access (supports multiple coupons for same feature - they extend access)
    const userAccessDoc = await getDoc(doc(db, 'userFeatureAccess', userId));
    let newExpiresAt = expiresAt;

    if (userAccessDoc.exists()) {
      const existingAccess = userAccessDoc.data()[coupon.feature];
      if (existingAccess?.active && existingAccess?.expiresAt) {
        // Extend existing access
        const existingExpiry = new Date(existingAccess.expiresAt);
        const now = new Date();
        const startFrom = existingExpiry > now ? existingExpiry : now;
        
        if (coupon.duration !== 'lifetime') {
          newExpiresAt = new Date(startFrom.getTime() + coupon.duration * 24 * 60 * 60 * 1000).toISOString();
        }
      }
    }

    await setDoc(doc(db, 'userFeatureAccess', userId), {
      [coupon.feature]: {
        active: true,
        activatedAt: new Date().toISOString(),
        expiresAt: newExpiresAt,
        lastCouponCode: couponCode,
        totalCouponsRedeemed: increment(1)
      }
    }, { merge: true });

    // Update coupon usage count
    await updateDoc(doc(db, 'coupons', couponCode), {
      usedCount: increment(1)
    });

    return {
      success: true,
      feature: coupon.feature,
      duration: coupon.duration,
      expiresAt: newExpiresAt,
      redemptionId
    };
  } catch (error) {
    console.error('Redeem coupon error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getUserFeatureAccess = async (userId) => {
  try {
    const accessDoc = await getDoc(doc(db, 'userFeatureAccess', userId));
    if (!accessDoc.exists()) {
      return {
        success: true,
        access: {}
      };
    }
    return {
      success: true,
      access: accessDoc.data()
    };
  } catch (error) {
    console.error('Get user feature access error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all coupon redemptions (for admin reporting)
export const getAllCouponRedemptions = async () => {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'couponRedemptions'), orderBy('redeemedAt', 'desc'))
    );
    const redemptions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, redemptions };
  } catch (error) {
    console.error('Get all redemptions error:', error);
    return { success: false, error: error.message, redemptions: [] };
  }
};

// Get redemptions by user
export const getUserCouponRedemptions = async (userId) => {
  try {
    const snapshot = await getDocs(
      query(
        collection(db, 'couponRedemptions'),
        where('userId', '==', userId),
        orderBy('redeemedAt', 'desc')
      )
    );
    const redemptions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, redemptions };
  } catch (error) {
    console.error('Get user redemptions error:', error);
    return { success: false, error: error.message, redemptions: [] };
  }
};

// Get redemptions by coupon
export const getCouponRedemptions = async (couponCode) => {
  try {
    const snapshot = await getDocs(
      query(
        collection(db, 'couponRedemptions'),
        where('couponCode', '==', couponCode),
        orderBy('redeemedAt', 'desc')
      )
    );
    const redemptions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, redemptions };
  } catch (error) {
    console.error('Get coupon redemptions error:', error);
    return { success: false, error: error.message, redemptions: [] };
  }
};

// Admin assign coupon to user
export const adminAssignCouponToUser = async (userId, couponCode, userEmail) => {
  return await validateAndRedeemCoupon(userId, couponCode, true, userEmail);
};

/** Sync coupon.usedCount to actual redemption records (fixes drift after failed assigns) */
export const resetCouponUsageCount = async (couponCode) => {
  try {
    const redemptionsSnapshot = await getDocs(
      query(
        collection(db, 'couponRedemptions'),
        where('couponCode', '==', couponCode)
      )
    );
    const usedCount = redemptionsSnapshot.size;

    await updateDoc(doc(db, 'coupons', couponCode), {
      usedCount,
      updatedAt: serverTimestamp()
    });

    return { success: true, usedCount };
  } catch (error) {
    console.error('Reset coupon usage count error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Enable/Disable feature access for a user (Admin only)
 * @param {string} userId - User ID
 * @param {string} feature - Feature name (e.g., 'offline_jobs')
 * @param {boolean} enabled - Whether to enable or disable
 * @param {string|null} duration - 'lifetime' or number of days (null for lifetime)
 */
export const updateUserFeatureAccess = async (userId, feature, enabled, duration = 'lifetime') => {
  try {
    const accessRef = doc(db, 'userFeatureAccess', userId);
    
    if (!enabled) {
      // Disable the feature
      await setDoc(accessRef, {
        [feature]: {
          active: false,
          disabledAt: new Date().toISOString(),
          disabledBy: 'admin'
        }
      }, { merge: true });
      
      return {
        success: true,
        message: 'Feature access disabled successfully'
      };
    }
    
    // Enable the feature
    const expiresAt = duration === 'lifetime' || !duration
      ? null 
      : new Date(Date.now() + Number(duration) * 24 * 60 * 60 * 1000).toISOString();
    
    await setDoc(accessRef, {
      [feature]: {
        active: true,
        activatedAt: new Date().toISOString(),
        expiresAt,
        grantedBy: 'admin'
      }
    }, { merge: true });
    
    return {
      success: true,
      message: 'Feature access enabled successfully'
    };
  } catch (error) {
    console.error('Update user feature access error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * MESSAGES
 */
export const sendMessageFirebase = async (messageData) => {
  try {
    console.log('💬 Sending message:', messageData);
    const messageRef = await addDoc(collection(db, 'messages'), {
      ...messageData,
      createdAt: serverTimestamp(),
      read: false
    });
    
    console.log('✅ Message sent:', messageRef.id);
    return {
      success: true,
      messageId: messageRef.id
    };
  } catch (error) {
    console.error('❌ Send message error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all messages for a specific booking
export const getMessagesByBooking = async (bookingId) => {
  try {
    console.log('💬 Fetching messages for booking:', bookingId);
    const q = query(
      collection(db, 'messages'),
      where('bookingId', '==', bookingId),
      orderBy('createdAt', 'asc')
    );
    const snapshot = await getDocs(q);
    const messages = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
      };
    });
    
    console.log('✅ Found messages:', messages.length);
    return {
      success: true,
      messages
    };
  } catch (error) {
    console.error('❌ Get messages error:', error);
    return {
      success: false,
      error: error.message,
      messages: []
    };
  }
};

// Get all messages for a user (across all bookings)
export const getMessagesByUser = async (userId) => {
  try {
    console.log('💬 Fetching messages for user:', userId);
    
    // Query messages where user is either sender or receiver
    const q1 = query(
      collection(db, 'messages'),
      where('fromUserId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const q2 = query(
      collection(db, 'messages'),
      where('toUserId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    // Combine and deduplicate
    const messagesMap = new Map();
    
    [...snapshot1.docs, ...snapshot2.docs].forEach(doc => {
      const data = doc.data();
      messagesMap.set(doc.id, {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString()
      });
    });
    
    const messages = Array.from(messagesMap.values()).sort((a, b) => {
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
    
    console.log('✅ Found messages:', messages.length);
    return {
      success: true,
      messages
    };
  } catch (error) {
    console.error('❌ Get user messages error:', error);
    return {
      success: false,
      error: error.message,
      messages: []
    };
  }
};

// Mark message as read
export const markMessageAsRead = async (messageId) => {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, {
      read: true,
      readAt: serverTimestamp()
    });
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Mark message as read error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete a message
export const deleteMessage = async (messageId) => {
  try {
    await deleteDoc(doc(db, 'messages', messageId));
    return {
      success: true
    };
  } catch (error) {
    console.error('Delete message error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Real-time listener for user messages
export const subscribeToUserMessages = (userId, callback) => {
  try {
    console.log('🔔 Setting up real-time message listener for user:', userId);
    
    // Query messages where user is either sender or receiver
    const q1 = query(
      collection(db, 'messages'),
      where('fromUserId', '==', userId),
      orderBy('createdAt', 'asc')
    );
    
    const q2 = query(
      collection(db, 'messages'),
      where('toUserId', '==', userId),
      orderBy('createdAt', 'asc')
    );
    
    // Set up listeners for both queries
    const unsubscribe1 = onSnapshot(q1, (snapshot) => {
      console.log('🔔 Messages update (sent):', snapshot.docs.length);
      callback();
    });
    
    const unsubscribe2 = onSnapshot(q2, (snapshot) => {
      console.log('🔔 Messages update (received):', snapshot.docs.length);
      callback();
    });
    
    // Return combined unsubscribe function
    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  } catch (error) {
    console.error('❌ Error setting up message listener:', error);
    return () => {}; // Return empty unsubscribe function
  }
};

/**
 * CAREGIVERS
 */
export const getAllCaregivers = async () => {
  try {
    const caregiverQuery = query(
      collection(db, 'users'),
      where('role', '==', 'caregiver')
    );
    
    const snapshot = await getDocs(caregiverQuery);

    // Pull every service listing once and group by caregiver so search can
    // filter by service without an extra read per caregiver.
    const servicesByCaregiver = new Map();
    try {
      const servicesSnapshot = await getDocs(collection(db, 'services'));
      servicesSnapshot.docs.forEach(serviceDoc => {
        const service = serviceDoc.data();
        if (!service.caregiverId) return;
        if (service.available === false) return;

        const existing = servicesByCaregiver.get(service.caregiverId) || [];
        existing.push({ id: serviceDoc.id, ...service });
        servicesByCaregiver.set(service.caregiverId, existing);
      });
    } catch (servicesError) {
      // Service listings are supplementary — a failure here should not blank the
      // caregiver directory, it just means no service-based filtering.
      console.error('Get caregiver services error:', servicesError);
    }

    const caregivers = snapshot.docs.map(doc => {
      const userData = doc.data();
      const serviceListings = servicesByCaregiver.get(doc.id) || [];
      const serviceSlugs = toServiceSlugs([
        ...serviceListings,
        ...(Array.isArray(userData.services) ? userData.services : [])
      ]);
      
      // Transform user data to caregiver format expected by UI
      return {
        id: doc.id,
        user: {
          id: doc.id,
          name: userData.name || userData.displayName || 'Pet Caregiver',
          avatar: userData.avatar || userData.photoURL || '',
          email: userData.email
        },
        services: serviceListings,
        serviceSlugs,
        // Caregiver-specific fields with safe defaults
        title: userData.title || 'Professional Pet Caregiver',
        bio: userData.bio || 'Passionate about pet care and dedicated to providing the best service for your furry friends.',
        rating: userData.rating || 4.5,
        reviewCount: userData.reviewCount || 0,
        homePhotos: userData.homePhotos || [],
        speciesAccepted: userData.speciesAccepted || ['Dog', 'Cat'],
        badges: userData.badges || ['verified'],
        maxPets: userData.maxPets || 3,
        hourlyRate: userData.hourlyRate || 250,
        isBackgroundChecked: userData.isBackgroundChecked || false,
        location: userData.location || '',
        isAvailable: userData.isAvailable !== false // Default to true
      };
    });
    
    return {
      success: true,
      caregivers
    };
  } catch (error) {
    console.error('Get caregivers error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const getCaregiverById = async (caregiverId) => {
  try {
    const docSnap = await getDoc(doc(db, 'users', caregiverId));
    
    if (!docSnap.exists()) {
      return {
        success: false,
        error: 'Caregiver not found'
      };
    }
    
    return {
      success: true,
      caregiver: {
        id: docSnap.id,
        ...docSnap.data()
      }
    };
  } catch (error) {
    console.error('Get caregiver by ID error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const updateCaregiverProfile = async (caregiverId, updates) => {
  try {
    await updateDoc(doc(db, 'users', caregiverId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Update caregiver profile error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Generic update user function
 */
export const updateUser = async (userId, updates) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Update user error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user subscription price (Admin only)
 */
export const updateUserSubscriptionPrice = async (userId, subscriptionPrice) => {
  try {
    // If subscriptionPrice is null/undefined, remove the field (set to null)
    // If it has a value, convert to number
    const updateData = {
      updatedAt: serverTimestamp()
    };
    
    if (subscriptionPrice === null || subscriptionPrice === undefined || subscriptionPrice === '') {
      updateData.subscriptionPrice = null; // Explicitly set to null to remove subscription
    } else {
      updateData.subscriptionPrice = Number(subscriptionPrice);
    }
    
    await updateDoc(doc(db, 'users', userId), updateData);
    
    return {
      success: true,
      message: subscriptionPrice ? 'Subscription price updated successfully' : 'Subscription removed successfully'
    };
  } catch (error) {
    console.error('Update subscription price error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update user's basic role (Admin only)
 */
export const updateUserRole = async (userId, newRole) => {
  try {
    const validRoles = ['owner', 'caregiver', 'admin'];
    if (!validRoles.includes(newRole)) {
      return {
        success: false,
        error: 'Invalid role. Must be owner, caregiver, or admin.'
      };
    }

    await updateDoc(doc(db, 'users', userId), {
      role: newRole,
      updatedAt: serverTimestamp()
    });
    
    return {
      success: true,
      message: `User role updated to ${newRole} successfully`
    };
  } catch (error) {
    console.error('Update user role error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get all users (Admin only)
 */
export const getAllUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = [];
    
    usersSnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      success: true,
      users
    };
  } catch (error) {
    console.error('Get all users error:', error);
    return {
      success: false,
      error: error.message,
      users: []
    };
  }
};

/**
 * ADMIN - USER MANAGEMENT
 */

// Suspend a user account
export const suspendUser = async (userId, reason = '') => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      status: 'suspended',
      suspendedAt: serverTimestamp(),
      suspensionReason: reason,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'User suspended successfully' };
  } catch (error) {
    console.error('Suspend user error:', error);
    return { success: false, error: error.message };
  }
};

// Activate a suspended user account
export const activateUser = async (userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      status: 'active',
      suspendedAt: null,
      suspensionReason: null,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'User activated successfully' };
  } catch (error) {
    console.error('Activate user error:', error);
    return { success: false, error: error.message };
  }
};

// Delete a user and all associated data
export const deleteUser = async (userId) => {
  try {
    // Delete user's pets
    const petsQuery = query(collection(db, 'pets'), where('ownerId', '==', userId));
    const petsSnapshot = await getDocs(petsQuery);
    petsSnapshot.forEach(async (petDoc) => {
      await deleteDoc(petDoc.ref);
    });

    // Delete user's bookings
    const bookingsQuery = query(
      collection(db, 'bookings'), 
      where('ownerId', '==', userId)
    );
    const bookingsSnapshot = await getDocs(bookingsQuery);
    bookingsSnapshot.forEach(async (bookingDoc) => {
      await deleteDoc(bookingDoc.ref);
    });

    // Delete user document
    await deleteDoc(doc(db, 'users', userId));
    
    return { success: true, message: 'User deleted successfully' };
  } catch (error) {
    console.error('Delete user error:', error);
    return { success: false, error: error.message };
  }
};

// Create a new user account by admin using Cloud Function
export const createUserByAdmin = async (userData) => {
  try {
    const { email, password, username, phone, role } = userData;
    
    // Call the Cloud Function
    const createUserFunction = httpsCallable(functions, 'createUserByAdmin');
    const result = await createUserFunction({
      email,
      password,
      username,
      phone,
      role
    });
    
    // Return the result from Cloud Function
    return {
      success: true,
      message: 'User created successfully! They can now login directly.',
      user: result.data.user,
      credentials: {
        email: result.data.credentials.email,
        tempPassword: result.data.credentials.tempPassword,
        uid: result.data.credentials.uid,
        loginUrl: `${window.location.origin}/login`
      }
    };
  } catch (error) {
    console.error('Create user by admin error:', error);
    
    // Extract error message from Cloud Function error
    let errorMessage = 'Failed to create user';
    if (error.code === 'functions/already-exists') {
      errorMessage = error.message;
    } else if (error.code === 'functions/invalid-argument') {
      errorMessage = error.message;
    } else if (error.code === 'functions/permission-denied') {
      errorMessage = 'You do not have permission to create users';
    } else if (error.code === 'functions/unauthenticated') {
      errorMessage = 'You must be logged in to create users';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

/**
 * ADMIN - CAREGIVER MANAGEMENT
 */


// Verification system removed - all caregivers approved by default

/**
 * ADMIN - BOOKING MANAGEMENT
 */

// Approve a booking request (admin override)
export const approveBooking = async (bookingId) => {
  try {
    await updateDoc(doc(db, 'bookings', bookingId), {
      status: 'confirmed',
      approvedAt: serverTimestamp(),
      approvedBy: 'admin',
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Booking approved successfully' };
  } catch (error) {
    console.error('Approve booking error:', error);
    return { success: false, error: error.message };
  }
};

// Cancel a booking and process refund
export const cancelBookingAdmin = async (bookingId, reason = '', refundAmount = 0) => {
  try {
    await updateDoc(doc(db, 'bookings', bookingId), {
      status: 'cancelled',
      cancelledAt: serverTimestamp(),
      cancelledBy: 'admin',
      cancellationReason: reason,
      refundAmount,
      refundProcessed: refundAmount > 0,
      updatedAt: serverTimestamp()
    });
    
    // Log the refund transaction
    if (refundAmount > 0) {
      await addDoc(collection(db, 'transactions'), {
        bookingId,
        type: 'refund',
        amount: refundAmount,
        processedBy: 'admin',
        createdAt: serverTimestamp()
      });
    }
    
    return { success: true, message: 'Booking cancelled and refund processed' };
  } catch (error) {
    console.error('Cancel booking error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ADMIN - PLATFORM SETTINGS
 */

// Get platform settings
export const getPlatformSettings = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, 'platform', 'settings'));
    
    if (settingsDoc.exists()) {
      return { success: true, settings: settingsDoc.data() };
    }
    
    // Return default settings if none exist
    return {
      success: true,
      settings: {
        commissionRate: 15,
        currency: 'INR',
        minBookingAmount: 500,
        maxBookingAmount: 50000,
        cancellationFeePercent: 10,
        platformFeaturesEnabled: true,
        defaultThemeId: 'trust-blue',
      }
    };
  } catch (error) {
    console.error('Get platform settings error:', error);
    return { success: false, error: error.message };
  }
};

// Update platform settings
export const updatePlatformSettings = async (settings) => {
  try {
    await setDoc(doc(db, 'platform', 'settings'), {
      ...settings,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return { success: true, message: 'Platform settings updated successfully' };
  } catch (error) {
    console.error('Update platform settings error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ADMIN - FEATURE FLAGS
 */

// Get feature flags
export const getFeatureFlags = async () => {
  try {
    const flagsDoc = await getDoc(doc(db, 'platform', 'featureFlags'));
    
    if (flagsDoc.exists()) {
      return { success: true, flags: flagsDoc.data() };
    }
    
    // Return default flags if none exist
    return {
      success: true,
      flags: {
        'ai-matching': true,
        'realtime-chat': true,
        'video-verification': false,
        'dynamic-pricing': false,
        'insurance-coverage': true
      }
    };
  } catch (error) {
    console.error('Get feature flags error:', error);
    return { success: false, error: error.message };
  }
};

// Update a feature flag
export const updateFeatureFlag = async (flagKey, enabled) => {
  try {
    await setDoc(doc(db, 'platform', 'featureFlags'), {
      [flagKey]: enabled,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return { success: true, message: `Feature flag ${flagKey} updated` };
  } catch (error) {
    console.error('Update feature flag error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ADMIN - ANALYTICS & REPORTING
 */

// Get platform analytics
export const getPlatformAnalytics = async (startDate, endDate) => {
  try {
    // This would fetch analytics data within a date range
    // For now, return a success message
    return { 
      success: true, 
      message: 'Analytics data fetched successfully',
      data: {}
    };
  } catch (error) {
    console.error('Get platform analytics error:', error);
    return { success: false, error: error.message };
  }
};

// Export data for reporting
export const exportPlatformData = async (dataType, format = 'csv') => {
  try {
    // This would export data in specified format
    return { 
      success: true, 
      message: `${dataType} data exported as ${format}`,
      downloadUrl: '#'
    };
  } catch (error) {
    console.error('Export platform data error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ADMIN - CONTENT MANAGEMENT
 */

// Create/Update blog post
export const saveBlogPost = async (postId, postData) => {
  try {
    if (postId) {
      await updateDoc(doc(db, 'blogPosts', postId), {
        ...postData,
        updatedAt: serverTimestamp()
      });
    } else {
      await addDoc(collection(db, 'blogPosts'), {
        ...postData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return { success: true, message: 'Blog post saved successfully' };
  } catch (error) {
    console.error('Save blog post error:', error);
    return { success: false, error: error.message };
  }
};

// Delete blog post
export const deleteBlogPost = async (postId) => {
  try {
    await deleteDoc(doc(db, 'blogPosts', postId));
    return { success: true, message: 'Blog post deleted successfully' };
  } catch (error) {
    console.error('Delete blog post error:', error);
    return { success: false, error: error.message };
  }
};

// Create/Update FAQ
export const saveFAQ = async (faqId, faqData) => {
  try {
    if (faqId) {
      await updateDoc(doc(db, 'faqs', faqId), {
        ...faqData,
        updatedAt: serverTimestamp()
      });
    } else {
      await addDoc(collection(db, 'faqs'), {
        ...faqData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
    
    return { success: true, message: 'FAQ saved successfully' };
  } catch (error) {
    console.error('Save FAQ error:', error);
    return { success: false, error: error.message };
  }
};

// Delete FAQ
export const deleteFAQ = async (faqId) => {
  try {
    await deleteDoc(doc(db, 'faqs', faqId));
    return { success: true, message: 'FAQ deleted successfully' };
  } catch (error) {
    console.error('Delete FAQ error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ADMIN - SUPPORT & TICKETS
 */

// Get all support tickets
export const getAllTickets = async () => {
  try {
    const ticketsSnapshot = await getDocs(
      query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'))
    );
    
    const tickets = [];
    ticketsSnapshot.forEach((doc) => {
      tickets.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, tickets };
  } catch (error) {
    console.error('Get all tickets error:', error);
    return { success: false, error: error.message, tickets: [] };
  }
};

// Update ticket status
export const updateTicketStatus = async (ticketId, status, response = '') => {
  try {
    await updateDoc(doc(db, 'supportTickets', ticketId), {
      status,
      adminResponse: response,
      resolvedAt: status === 'resolved' ? serverTimestamp() : null,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Ticket status updated' };
  } catch (error) {
    console.error('Update ticket status error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ADMIN - ROLE & PERMISSION MANAGEMENT
 */

// Get all roles
export const getAllRoles = async () => {
  try {
    const rolesSnapshot = await getDocs(collection(db, 'adminRoles'));
    const roles = [];
    
    rolesSnapshot.forEach((doc) => {
      roles.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, roles };
  } catch (error) {
    console.error('Get all roles error:', error);
    return { success: false, error: error.message, roles: [] };
  }
};

// Create custom role
export const createAdminRole = async (roleData) => {
  try {
    const roleRef = await addDoc(collection(db, 'adminRoles'), {
      ...roleData,
      isSystem: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, roleId: roleRef.id, message: 'Role created successfully' };
  } catch (error) {
    console.error('Create role error:', error);
    return { success: false, error: error.message };
  }
};

// Update role
export const updateAdminRole = async (roleId, updates) => {
  try {
    const roleDoc = await getDoc(doc(db, 'adminRoles', roleId));
    
    if (!roleDoc.exists()) {
      return { success: false, error: 'Role not found' };
    }
    
    // Prevent editing system roles
    if (roleDoc.data().isSystem) {
      return { success: false, error: 'Cannot edit system roles' };
    }
    
    await updateDoc(doc(db, 'adminRoles', roleId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Role updated successfully' };
  } catch (error) {
    console.error('Update role error:', error);
    return { success: false, error: error.message };
  }
};

// Delete role
export const deleteAdminRole = async (roleId) => {
  try {
    const roleDoc = await getDoc(doc(db, 'adminRoles', roleId));
    
    if (!roleDoc.exists()) {
      return { success: false, error: 'Role not found' };
    }
    
    // Prevent deleting system roles
    if (roleDoc.data().isSystem) {
      return { success: false, error: 'Cannot delete system roles' };
    }
    
    // Check if any users have this role
    const usersWithRole = await getDocs(
      query(collection(db, 'users'), where('adminRole', '==', roleId))
    );
    
    if (!usersWithRole.empty) {
      return { 
        success: false, 
        error: `Cannot delete role - ${usersWithRole.size} user(s) still have this role` 
      };
    }
    
    await deleteDoc(doc(db, 'adminRoles', roleId));
    
    return { success: true, message: 'Role deleted successfully' };
  } catch (error) {
    console.error('Delete role error:', error);
    return { success: false, error: error.message };
  }
};

// Assign role to user
export const assignRoleToUser = async (userId, roleId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      adminRole: roleId,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Role assigned successfully' };
  } catch (error) {
    console.error('Assign role error:', error);
    return { success: false, error: error.message };
  }
};

// Get user permissions
export const getUserPermissions = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found', permissions: [] };
    }
    
    const userData = userDoc.data();
    
    // If user has customPermissions, use those
    if (userData.customPermissions && userData.customPermissions.length > 0) {
      return { success: true, permissions: userData.customPermissions };
    }
    
    // Otherwise get permissions from role
    if (userData.adminRole) {
      const roleDoc = await getDoc(doc(db, 'adminRoles', userData.adminRole));
      
      if (roleDoc.exists()) {
        return { success: true, permissions: roleDoc.data().permissions || [] };
      }
    }
    
    // Default: no permissions
    return { success: true, permissions: [] };
  } catch (error) {
    console.error('Get user permissions error:', error);
    return { success: false, error: error.message, permissions: [] };
  }
};

// Set custom permissions for user
export const setUserCustomPermissions = async (userId, permissions) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      customPermissions: permissions,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Custom permissions set successfully' };
  } catch (error) {
    console.error('Set custom permissions error:', error);
    return { success: false, error: error.message };
  }
};

// Initialize predefined roles (run once)
export const initializePredefinedRoles = async (predefinedRoles) => {
  try {
    const batch = [];
    
    for (const [key, roleData] of Object.entries(predefinedRoles)) {
      const roleRef = doc(db, 'adminRoles', roleData.id);
      const roleDoc = await getDoc(roleRef);
      
      // Only create if doesn't exist
      if (!roleDoc.exists()) {
        await setDoc(roleRef, {
          ...roleData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        batch.push(roleData.id);
      }
    }
    
    return { 
      success: true, 
      message: `Initialized ${batch.length} predefined roles`,
      initialized: batch
    };
  } catch (error) {
    console.error('Initialize roles error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * USER PROFILES
 */

// Create user profile
export const createUserProfile = async (userId, profileData) => {
  try {
    const profileRef = doc(db, 'userProfiles', userId);
    await setDoc(profileRef, {
      userId,
      alias: profileData.alias || '',
      bio: profileData.bio || '',
      profilePhoto: profileData.profilePhoto || '',
      coverPhoto: profileData.coverPhoto || '',
      isPublic: profileData.isPublic !== undefined ? profileData.isPublic : true,
      location: profileData.location || '',
      website: profileData.website || '',
      instagram: profileData.instagram || '',
      facebook: profileData.facebook || '',
      totalPhotos: 0,
      photos: [],
      joinedDate: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Profile created successfully' };
  } catch (error) {
    console.error('Create profile error:', error);
    return { success: false, error: error.message };
  }
};

// Get user profile
export const getUserProfile = async (userId) => {
  try {
    const profileDoc = await getDoc(doc(db, 'userProfiles', userId));
    
    if (!profileDoc.exists()) {
      return { success: false, error: 'Profile not found', profile: null };
    }
    
    return { 
      success: true, 
      profile: { id: profileDoc.id, ...profileDoc.data() }
    };
  } catch (error) {
    console.error('Get profile error:', error);
    return { success: false, error: error.message, profile: null };
  }
};

// Update user profile
export const updateUserProfile = async (userId, updates) => {
  try {
    const profileRef = doc(db, 'userProfiles', userId);
    await updateDoc(profileRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Profile updated successfully' };
  } catch (error) {
    console.error('Update profile error:', error);
    return { success: false, error: error.message };
  }
};

// Update privacy settings
export const updatePrivacySettings = async (userId, isPublic) => {
  try {
    const profileRef = doc(db, 'userProfiles', userId);
    await updateDoc(profileRef, {
      isPublic,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Privacy settings updated successfully' };
  } catch (error) {
    console.error('Update privacy error:', error);
    return { success: false, error: error.message };
  }
};

// Add photo to profile
export const addProfilePhoto = async (userId, photoUrl) => {
  try {
    const profileRef = doc(db, 'userProfiles', userId);
    const profileDoc = await getDoc(profileRef);
    
    if (!profileDoc.exists()) {
      return { success: false, error: 'Profile not found' };
    }
    
    const currentPhotos = profileDoc.data().photos || [];
    const totalPhotos = profileDoc.data().totalPhotos || 0;
    
    if (totalPhotos >= 10) {
      return { success: false, error: 'Maximum 10 photos allowed' };
    }
    
    await updateDoc(profileRef, {
      photos: [...currentPhotos, photoUrl],
      totalPhotos: totalPhotos + 1,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Photo added successfully' };
  } catch (error) {
    console.error('Add photo error:', error);
    return { success: false, error: error.message };
  }
};

// Remove photo from profile
export const removeProfilePhoto = async (userId, photoUrl) => {
  try {
    const profileRef = doc(db, 'userProfiles', userId);
    const profileDoc = await getDoc(profileRef);
    
    if (!profileDoc.exists()) {
      return { success: false, error: 'Profile not found' };
    }
    
    const currentPhotos = profileDoc.data().photos || [];
    const updatedPhotos = currentPhotos.filter(p => p !== photoUrl);
    
    await updateDoc(profileRef, {
      photos: updatedPhotos,
      totalPhotos: updatedPhotos.length,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Photo removed successfully' };
  } catch (error) {
    console.error('Remove photo error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ACTIVITIES
 */

// Create activity
export const createActivity = async (activityData) => {
  try {
    const activityRef = await addDoc(collection(db, 'activities'), {
      userId: activityData.userId,
      activityType: activityData.activityType,
      description: activityData.description,
      relatedId: activityData.relatedId || null,
      isPublic: activityData.isPublic !== undefined ? activityData.isPublic : true,
      timestamp: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    
    return { success: true, activityId: activityRef.id };
  } catch (error) {
    console.error('Create activity error:', error);
    return { success: false, error: error.message };
  }
};

// Get user activities
export const getUserActivities = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'activities'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const activities = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, activities };
  } catch (error) {
    console.error('Get user activities error:', error);
    return { success: false, error: error.message, activities: [] };
  }
};

// Get public activities
export const getPublicActivities = async (limitCount = 100) => {
  try {
    const q = query(
      collection(db, 'activities'),
      where('isPublic', '==', true),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const activities = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, activities };
  } catch (error) {
    console.error('Get public activities error:', error);
    return { success: false, error: error.message, activities: [] };
  }
};

// Delete activity
export const deleteActivity = async (activityId) => {
  try {
    await deleteDoc(doc(db, 'activities', activityId));
    return { success: true, message: 'Activity deleted successfully' };
  } catch (error) {
    console.error('Delete activity error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * FOLLOWERS
 */

// Follow a user
export const followUser = async (followerId, followingId) => {
  try {
    if (followerId === followingId) {
      return { success: false, error: 'Cannot follow yourself' };
    }
    
    const followId = `${followerId}_${followingId}`;
    const followRef = doc(db, 'followers', followId);
    
    // Check if already following
    const followDoc = await getDoc(followRef);
    if (followDoc.exists()) {
      return { success: false, error: 'Already following this user' };
    }
    
    await setDoc(followRef, {
      followerId,
      followingId,
      followedAt: serverTimestamp()
    });
    
    // Create activity for follower milestone
    const followingCount = await getFollowingCount(followerId);
    if ([10, 50, 100, 500, 1000].includes(followingCount)) {
      await createActivity({
        userId: followerId,
        activityType: 'follower_milestone',
        description: `Reached ${followingCount} following`,
        isPublic: true
      });
    }
    
    return { success: true, message: 'Successfully followed user' };
  } catch (error) {
    console.error('Follow user error:', error);
    return { success: false, error: error.message };
  }
};

// Unfollow a user
export const unfollowUser = async (followerId, followingId) => {
  try {
    const followId = `${followerId}_${followingId}`;
    await deleteDoc(doc(db, 'followers', followId));
    
    return { success: true, message: 'Successfully unfollowed user' };
  } catch (error) {
    console.error('Unfollow user error:', error);
    return { success: false, error: error.message };
  }
};

// Check if following
export const isFollowing = async (followerId, followingId) => {
  try {
    const followId = `${followerId}_${followingId}`;
    const followDoc = await getDoc(doc(db, 'followers', followId));
    
    return { success: true, isFollowing: followDoc.exists() };
  } catch (error) {
    console.error('Check following error:', error);
    return { success: false, error: error.message, isFollowing: false };
  }
};

// Get followers
export const getFollowers = async (userId, limitCount = 100) => {
  try {
    const q = query(
      collection(db, 'followers'),
      where('followingId', '==', userId),
      orderBy('followedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const followers = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, followers };
  } catch (error) {
    console.error('Get followers error:', error);
    return { success: false, error: error.message, followers: [] };
  }
};

// Get following
export const getFollowing = async (userId, limitCount = 100) => {
  try {
    const q = query(
      collection(db, 'followers'),
      where('followerId', '==', userId),
      orderBy('followedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(q);
    const following = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, following };
  } catch (error) {
    console.error('Get following error:', error);
    return { success: false, error: error.message, following: [] };
  }
};

// Get follower count
export const getFollowerCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'followers'),
      where('followingId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Get follower count error:', error);
    return 0;
  }
};

// Get following count
export const getFollowingCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'followers'),
      where('followerId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Get following count error:', error);
    return 0;
  }
};

/**
 * BADGES
 */

// Award badge
export const awardBadge = async (userId, badgeId, reason = 'Badge earned') => {
  try {
    // Check if user already has this badge
    const q = query(
      collection(db, 'badges'),
      where('userId', '==', userId),
      where('badgeId', '==', badgeId)
    );
    
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return { success: false, error: 'User already has this badge' };
    }
    
    const badgeRef = await addDoc(collection(db, 'badges'), {
      userId,
      badgeId,
      reason,
      earnedAt: serverTimestamp()
    });
    
    // Create activity for badge earned
    await createActivity({
      userId,
      activityType: 'badge_earned',
      description: `Earned a new badge`,
      relatedId: badgeRef.id,
      isPublic: true
    });
    
    return { success: true, id: badgeRef.id };
  } catch (error) {
    console.error('Award badge error:', error);
    return { success: false, error: error.message };
  }
};

// Get user badges
export const getUserBadges = async (userId) => {
  try {
    const q = query(
      collection(db, 'badges'),
      where('userId', '==', userId),
      orderBy('earnedAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const badges = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, badges };
  } catch (error) {
    console.error('Get badges error:', error);
    return { success: false, error: error.message, badges: [] };
  }
};

// Get all badges (admin only)
export const getAllBadges = async () => {
  try {
    const q = query(
      collection(db, 'badges'),
      orderBy('earnedAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const badges = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, badges };
  } catch (error) {
    console.error('Get all badges error:', error);
    return { success: false, error: error.message, badges: [] };
  }
};

// Revoke badge (admin only)
export const revokeBadge = async (badgeRecordId) => {
  try {
    await deleteDoc(doc(db, 'badges', badgeRecordId));
    return { success: true };
  } catch (error) {
    console.error('Revoke badge error:', error);
    return { success: false, error: error.message };
  }
};

// Check and award badges based on criteria
export const checkAndAwardBadges = async (userId, userData) => {
  try {
    const badgesAwarded = [];
    
    // Get existing badges
    const existingBadges = await getUserBadges(userId);
    const existingBadgeTypes = existingBadges.badges.map(b => b.badgeType);
    
    // Early Adopter - joined in first month (placeholder logic)
    if (!existingBadgeTypes.includes('early_adopter')) {
      // This would need actual launch date logic
      const joinDate = userData.createdAt?.toDate?.() || new Date();
      const launchDate = new Date('2024-01-01'); // Replace with actual launch date
      const monthAfterLaunch = new Date(launchDate);
      monthAfterLaunch.setMonth(monthAfterLaunch.getMonth() + 1);
      
      if (joinDate <= monthAfterLaunch) {
        await awardBadge(userId, 'early_adopter', {
          name: 'Early Adopter',
          description: 'Joined in the first month',
          icon: '🌟',
          color: '#f59e0b'
        });
        badgesAwarded.push('early_adopter');
      }
    }
    
    // Top Rated - average rating >= 4.8 with 10+ reviews
    if (!existingBadgeTypes.includes('top_rated') && userData.averageRating >= 4.8 && userData.totalReviews >= 10) {
      await awardBadge(userId, 'top_rated', {
        name: 'Top Rated',
        description: '4.8+ rating with 10+ reviews',
        icon: '⭐',
        color: '#eab308'
      });
      badgesAwarded.push('top_rated');
    }
    
    // Verified - phone + email verified
    if (!existingBadgeTypes.includes('verified') && userData.phoneVerified && userData.emailVerified) {
      await awardBadge(userId, 'verified', {
        name: 'Verified',
        description: 'Phone and email verified',
        icon: '✓',
        color: '#3b82f6'
      });
      badgesAwarded.push('verified');
    }
    
    // Super Host (Caregiver) - 50+ bookings completed
    if (!existingBadgeTypes.includes('super_host') && userData.role === 'caregiver' && userData.completedBookings >= 50) {
      await awardBadge(userId, 'super_host', {
        name: 'Super Host',
        description: '50+ bookings completed',
        icon: '🏆',
        color: '#8b5cf6'
      });
      badgesAwarded.push('super_host');
    }
    
    // Trusted Caregiver - 100+ bookings completed
    if (!existingBadgeTypes.includes('trusted_caregiver') && userData.role === 'caregiver' && userData.completedBookings >= 100) {
      await awardBadge(userId, 'trusted_caregiver', {
        name: 'Trusted Caregiver',
        description: '100+ bookings completed',
        icon: '💎',
        color: '#06b6d4'
      });
      badgesAwarded.push('trusted_caregiver');
    }
    
    // Pet Lover (Owner) - 3+ pets added
    if (!existingBadgeTypes.includes('pet_lover') && userData.role === 'owner' && userData.totalPets >= 3) {
      await awardBadge(userId, 'pet_lover', {
        name: 'Pet Lover',
        description: '3+ pets added',
        icon: '🐾',
        color: '#ec4899'
      });
      badgesAwarded.push('pet_lover');
    }
    
    // Active Member - 30+ days active, 5+ activities
    if (!existingBadgeTypes.includes('active_member')) {
      const userActivities = await getUserActivities(userId, 100);
      const joinDate = userData.createdAt?.toDate?.() || new Date();
      const daysSinceJoin = Math.floor((new Date() - joinDate) / (1000 * 60 * 60 * 24));
      
      if (daysSinceJoin >= 30 && userActivities.activities.length >= 5) {
        await awardBadge(userId, 'active_member', {
          name: 'Active Member',
          description: '30+ days active with 5+ activities',
          icon: '🔥',
          color: '#ef4444'
        });
        badgesAwarded.push('active_member');
      }
    }
    
    return { success: true, badgesAwarded };
  } catch (error) {
    console.error('Check and award badges error:', error);
    return { success: false, error: error.message, badgesAwarded: [] };
  }
};

/**
 * BLOG MANAGEMENT
 */

// Create blog post
export const createBlog = async (blogData) => {
  try {
    const blogRef = await addDoc(collection(db, 'blogs'), {
      ...blogData,
      views: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { success: true, id: blogRef.id, message: 'Blog post created successfully' };
  } catch (error) {
    console.error('Create blog error:', error);
    return { success: false, error: error.message };
  }
};

// Update blog post
export const updateBlog = async (blogId, updates) => {
  try {
    await updateDoc(doc(db, 'blogs', blogId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: 'Blog post updated successfully' };
  } catch (error) {
    console.error('Update blog error:', error);
    return { success: false, error: error.message };
  }
};

// Delete blog post
export const deleteBlog = async (blogId) => {
  try {
    await deleteDoc(doc(db, 'blogs', blogId));
    return { success: true, message: 'Blog post deleted successfully' };
  } catch (error) {
    console.error('Delete blog error:', error);
    return { success: false, error: error.message };
  }
};

// Get all blogs
export const getAllBlogs = async () => {
  try {
    const q = query(
      collection(db, 'blogs'),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const blogs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, blogs };
  } catch (error) {
    console.error('Get all blogs error:', error);
    return { success: false, error: error.message, blogs: [] };
  }
};

// Get published blogs only
export const getPublishedBlogs = async () => {
  try {
    const q = query(
      collection(db, 'blogs'),
      where('status', '==', 'published'),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const blogs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return { success: true, blogs };
  } catch (error) {
    console.error('Get published blogs error:', error);
    return { success: false, error: error.message, blogs: [] };
  }
};

// Get blog by slug (published only by default)
export const getBlogBySlug = async (slug, { includeDrafts = false } = {}) => {
  try {
    const constraints = [
      where('slug', '==', slug),
    ];
    if (!includeDrafts) {
      constraints.push(where('status', '==', 'published'));
    }

    const q = query(collection(db, 'blogs'), ...constraints, limit(1));
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return { success: false, error: 'Blog post not found', blog: null };
    }
    
    const blog = {
      id: snapshot.docs[0].id,
      ...snapshot.docs[0].data()
    };
    
    return { success: true, blog };
  } catch (error) {
    console.error('Get blog by slug error:', error);
    return { success: false, error: error.message, blog: null };
  }
};

// Increment blog views
export const incrementBlogViews = async (blogId) => {
  try {
    const blogRef = doc(db, 'blogs', blogId);
    const blogDoc = await getDoc(blogRef);
    
    if (blogDoc.exists()) {
      const currentViews = blogDoc.data().views || 0;
      await updateDoc(blogRef, {
        views: currentViews + 1
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Increment blog views error:', error);
    return { success: false, error: error.message };
  }
};

